import { ConnectButton } from '@rainbow-me/rainbowkit'
import { Droplets, Network, ArrowRight } from 'lucide-react'
import { useAccount, useChainId, useSwitchChain, useBalance } from 'wagmi'
import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { formatEther } from 'viem'
import { getChainById, supportedChains } from '@/config/wagmi'

export const Header = () => {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { switchChain, isPending: isSwitching } = useSwitchChain()
  const { data: balance, isLoading: isLoadingBalance } = useBalance({
    address,
    query: {
      refetchInterval: 10000, // Refetch every 10 seconds
      enabled: !!address && isConnected, // Only fetch if connected
    },
  })

  // Balance is still used for UI, but we no longer spam debug logs to the console.
  const [isLoading, setIsLoading] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [hasTriedAutoSwitch, setHasTriedAutoSwitch] = useState(false)

  const isLocalhost = chainId === 31337
  const arbitrumChain = supportedChains.find(chain => chain.id === 42161)
  const polygonChain = supportedChains.find(chain => chain.id === 137)
  const bscChain = supportedChains.find(chain => chain.id === 56)
  const currentChain = getChainById(chainId)
  const networkName = currentChain?.name || (chainId === 31337 ? 'Localhost' : 'Unknown')
  
  // Check if on mainnet (Arbitrum, Polygon or BSC)
  const isOnMainnet = chainId === 42161 || chainId === 137 || chainId === 56
  
  // Format balance for display
  const balanceFormatted = balance ? formatEther(balance.value) : '0'
  const balanceDisplay = balanceFormatted ? parseFloat(balanceFormatted).toFixed(4) : '0.0000'

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Soft suggestion: log when wallet is on a non-Arbitrum network, but do not force auto-switch
  useEffect(() => {
    if (!isConnected || !arbitrumChain || !chainId) return

    if (chainId !== arbitrumChain.id && !hasTriedAutoSwitch) {
      setHasTriedAutoSwitch(true)
      console.log('ℹ️ Wallet connected on non-Arbitrum network', {
        currentChainId: chainId,
        expectedChainId: arbitrumChain.id,
      })
    }
  }, [isConnected, chainId, arbitrumChain, hasTriedAutoSwitch])

  const requestFaucet = async () => {
    if (!address) {
      toast.error('Connect wallet first')
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch('http://127.0.0.1:8545', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'hardhat_setBalance',
          params: [address, '0x56BC75E2D63100000'],
          id: 1,
        }),
      })

      const result = await response.json()
      if (result.error) throw new Error(result.error.message)
      toast.success('Received 100 test ETH!')
    } catch (error) {
      toast.error('Failed to get test ETH')
    } finally {
      setIsLoading(false)
    }
  }

  const switchToNetwork = async (targetChain: (typeof supportedChains)[number] | undefined, networkName: string) => {
    if (!targetChain) {
      toast.error(`${networkName} chain not configured`)
      return
    }

    console.log(`🔄 Attempting to switch to ${networkName}...`, { chainId: targetChain.id })

    try {
      await switchChain({ chainId: targetChain.id })
      console.log(`✅ Successfully switched to ${networkName}`)
      toast.success(`Switched to ${networkName}`)
    } catch (error: any) {
      console.error('❌ Switch chain error:', error)
      
      // Error code 4902: Chain not added to wallet
      if (error?.code === 4902 || error?.message?.includes('not added')) {
        console.log('➕ Chain not in wallet, attempting to add...')
        try {
          const ethereum = (window as any).ethereum
          if (!ethereum) {
            toast.error('Wallet not found. Please install a Web3 wallet.')
            return
          }
          
          const rpcUrls = targetChain.id === 42161 
            ? ['https://arb1.arbitrum.io/rpc']
            : targetChain.id === 56
            ? ['https://bsc-rpc.publicnode.com']
            : ['https://polygon-rpc.com']
          
          const blockExplorerUrls = targetChain.id === 42161
            ? ['https://arbiscan.io']
            : targetChain.id === 56
            ? ['https://bscscan.com']
            : ['https://polygonscan.com']
          
          const chainParams = {
            chainId: `0x${targetChain.id.toString(16)}`,
            chainName: targetChain.name,
            nativeCurrency: targetChain.nativeCurrency,
            rpcUrls,
            blockExplorerUrls,
          }
          
          console.log('➕ Adding chain with params:', chainParams)
          
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [chainParams],
          })
          
          console.log('✅ Chain added successfully')
          toast.success(`${networkName} network added!`)
          
          // Try to switch again after adding
          setTimeout(async () => {
            try {
              await switchChain({ chainId: targetChain.id })
              toast.success(`Switched to ${networkName}`)
            } catch (retryError) {
              console.error('❌ Retry switch failed:', retryError)
            }
          }, 1000)
        } catch (addError: any) {
          console.error('❌ Failed to add chain:', addError)
          toast.error(`Failed to add ${networkName} network: ${addError?.message || 'Unknown error'}`)
        }
      } 
      // Error code 4001: User rejected
      else if (error?.code === 4001) {
        toast.error('Network switch rejected')
      }
      // Other errors
      else {
        const errorMsg = error?.message || error?.toString() || 'Unknown error'
        console.error('❌ Switch failed:', errorMsg)
        toast.error(`Failed to switch network: ${errorMsg}`)
      }
    }
  }

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-black/90 backdrop-blur-md border-b border-white/10' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#d4a853] rounded-lg flex items-center justify-center">
              <span className="text-black font-bold text-sm">N</span>
            </div>
            <span className="text-white font-semibold hidden sm:block">
              NFT<span className="text-[#d4a853]">WebToken</span>
            </span>
          </a>

          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-6">
            <a href="#mint" className="text-sm text-white/60 hover:text-white transition-colors">
              Mint
            </a>
            <a href="#gallery" className="text-sm text-white/60 hover:text-white transition-colors">
              Gallery
            </a>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Network Indicator */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
              <Network className="w-3 h-3 text-white/60" />
              <span className="text-xs text-white/60 font-medium">{networkName}</span>
            </div>

            {/* Network switch buttons - show when not on mainnet */}
            {!isOnMainnet && isConnected && (
              <div className="flex items-center gap-2">
                {arbitrumChain && chainId !== 42161 && (
                  <button
                    onClick={() => switchToNetwork(arbitrumChain, 'Arbitrum')}
                    disabled={isSwitching}
                    className="px-3 py-2 bg-[#28a0f0]/20 hover:bg-[#28a0f0]/30 border border-[#28a0f0]/30 text-[#28a0f0] text-xs font-medium rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Switch to Arbitrum mainnet"
                  >
                    <ArrowRight className="w-3 h-3" />
                    {isSwitching ? 'Switching...' : 'Arbitrum'}
                  </button>
                )}
                {polygonChain && chainId !== 137 && (
                  <button
                    onClick={() => switchToNetwork(polygonChain, 'Polygon')}
                    disabled={isSwitching}
                    className="px-3 py-2 bg-[#8247e5]/20 hover:bg-[#8247e5]/30 border border-[#8247e5]/30 text-[#8247e5] text-xs font-medium rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Switch to Polygon mainnet"
                  >
                    <ArrowRight className="w-3 h-3" />
                    {isSwitching ? 'Switching...' : 'Polygon'}
                  </button>
                )}
                {bscChain && chainId !== 56 && (
                  <button
                    onClick={() => switchToNetwork(bscChain, 'BSC')}
                    disabled={isSwitching}
                    className="px-3 py-2 bg-[#f0b90b]/20 hover:bg-[#f0b90b]/30 border border-[#f0b90b]/30 text-[#f0b90b] text-xs font-medium rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Switch to BSC mainnet"
                  >
                    <ArrowRight className="w-3 h-3" />
                    {isSwitching ? 'Switching...' : 'BSC'}
                  </button>
                )}
              </div>
            )}

            {/* Show balance if on mainnet and balance is available */}
            {isOnMainnet && isConnected && address && balance && (
              <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                chainId === 42161 
                  ? 'bg-[#28a0f0]/10 border border-[#28a0f0]/20' 
                  : chainId === 56
                  ? 'bg-[#f0b90b]/10 border border-[#f0b90b]/20'
                  : 'bg-[#8247e5]/10 border border-[#8247e5]/20'
              }`}>
                <span className={`text-xs font-semibold ${
                  chainId === 42161 ? 'text-[#28a0f0]' : chainId === 56 ? 'text-[#f0b90b]' : 'text-[#8247e5]'
                }`}>
                  {isLoadingBalance ? '...' : `${balanceDisplay} ${balance.symbol || 'ETH'}`}
                </span>
              </div>
            )}

            {/* Faucet - only on localhost */}
            {isLocalhost && address && (
              <button
                onClick={requestFaucet}
                disabled={isLoading}
                className="px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 text-cyan-400 text-xs font-medium rounded-lg transition-colors flex items-center gap-1"
              >
                <Droplets className="w-3 h-3" />
                {isLoading ? '...' : 'Faucet'}
              </button>
            )}
            
            {/* Connect Button with Network Switcher */}
            <ConnectButton 
              // Show wallet balance both on mobile (smallScreen) and desktop
              showBalance={{ smallScreen: true, largeScreen: true }}
              chainStatus="full"
              accountStatus={{ smallScreen: 'avatar', largeScreen: 'full' }}
            />
          </div>
        </div>
      </div>
    </header>
  )
}
